import { DVA_CARD_COLOUR } from '../constants'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import { trimValue } from '../../../utils/text-formatter'
import {
  dvaFileNumberValidator,
  ihiNumberValidator,
  medicareIrnValidator,
  medicareNumberValidator,
} from '../../../features/patient/utils/patientFieldsValidator'
import { LimitedPrescriberProfile } from '../../../features/users/UsersSlice'
import {
  Alert,
  AlertIcon,
  Button,
  Card,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Select,
  SimpleGrid,
  Spinner,
  VStack,
  Text,
} from '@chakra-ui/react'
import {
  resetMedicareValidationResult,
  resetDvaValidationResult,
  setDvaValidationResult,
  setMedicareValidationResult,
  resetIhiValidationResult,
  setIhiValidationResult,
} from '../PatientFormSlice'
import {
  HIStatus,
  IHIRecord,
  Patient,
  PatientWithOptionalId,
  Prescriber,
  RecordStatus,
} from '../../../types'
import { useFormContext, useWatch } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { IhiGender, PatientClient } from '../../../features/patient/PatientContextSlice'
import useApi from '../../../common/utils/api/useApi'
import InputField from '../../../components/InputField'
import { isValidDvaCardColour } from '../../../utils/validation-helpers/DvaValidate'
import { formatIhiResponse, formatIHIValidate, getWarning } from '../utils'
import useIHIRevalidateFieldsChange from '../hooks/useIHIRevalidateFieldsChange'
import { analyticsAction, trackEvent } from '../../../common/utils/analytics'

type IHIBuilderProps = {
  effectivePrescriber: LimitedPrescriberProfile | Prescriber | null
  defaultPatient?: Patient | PatientWithOptionalId
  isDisabled?: boolean
}

// The outcome only: the IHI number and the details it was looked up with stay
// out of analytics. formatIhiResponse reads a not-found reply as an Active
// record with no number. 01611 is the HI Service timing out, still there after
// the retries.
const trackIhiLookup = (
  method: 'medicare' | 'dva' | 'ihi',
  record?: {
    ihi_number: string
    ihi_status: HIStatus
    record_status: RecordStatus
    service_message: { codeField?: string } | null
  }
) => {
  if (!record || record.service_message?.codeField === '01611') {
    trackEvent('ihi_lookup', { method, result: 'error' })
  } else if (!record.ihi_number && record.ihi_status === 'Active') {
    trackEvent('ihi_lookup', { method, result: 'not_found' })
  } else {
    trackEvent('ihi_lookup', {
      method,
      result: 'found',
      ihiStatus: record.ihi_status,
      recordStatus: record.record_status,
    })
  }
}

const IHIBuilder = (props: IHIBuilderProps) => {
  const [ihiMethod, setIhiMethod] = useState<'generate' | 'manual'>('generate')
  const { defaultPatient, isDisabled = false } = props
  const originalIhiRecord: Omit<IHIRecord, 'created_at'> | undefined =
    defaultPatient?.ihi_number && defaultPatient?.ihi_status && defaultPatient?.record_status
      ? {
          ihi_number: defaultPatient.ihi_number,
          ihi_status: defaultPatient.ihi_status as HIStatus,
          record_status: defaultPatient.record_status as RecordStatus,
        }
      : undefined
  const {
    formState: { errors },
  } = useFormContext()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!originalIhiRecord) {
      return
    }

    const hasMedicare = !!(defaultPatient?.medicare_no && defaultPatient?.medicare_irn)
    const hasDva = !!defaultPatient?.dva_file_no

    if (hasMedicare) {
      dispatch(setMedicareValidationResult(originalIhiRecord))
    }
    if (hasDva) {
      dispatch(setDvaValidationResult(originalIhiRecord))
    }

    if (hasMedicare || hasDva) {
      return
    }

    if (defaultPatient?.ihi_number) {
      dispatch(setIhiValidationResult(originalIhiRecord))
      setIhiMethod('manual')
    }
  }, [
    defaultPatient?.medicare_no,
    defaultPatient?.medicare_irn,
    defaultPatient?.dva_file_no,
    defaultPatient?.ihi_number,
  ])

  return (
    <>
      {ihiMethod === 'generate' && (
        <VStack alignItems="stretch" flex={1} spacing={4}>
          <Heading as="h4" size="md">
            Medicare / DVA Information
          </Heading>
          <Text color="gray.500">Either Medicare and/or DVA required.</Text>
          <MedicareCard {...props} isDisabled={isDisabled} />
          <DvaCard {...props} isDisabled={isDisabled} />
          <Alert status="info" colorScheme="gray" gridColumn="1 / span 2">
            <AlertIcon />
            <Text as="div">
              IHI will be automatically generated when you enter Medicare or DVA information above.{' '}
              <br />
              <Text>
                Patients with IHI only:{' '}
                <Button
                  colorScheme="blue"
                  variant="link"
                  onClick={() => {
                    setIhiMethod('manual')
                  }}
                  {...analyticsAction('ihi_enter_manually')}
                  fontSize="sm"
                  display={'inline'}
                >
                  enter the IHI number manually
                </Button>
                .
              </Text>
              {errors.ihi_number && errors.ihi_number.type === 'manual' && (
                <Text color="red.500" fontWeight="bold" fontSize="md">
                  {errors.ihi_number?.message as string}
                </Text>
              )}
            </Text>
          </Alert>
        </VStack>
      )}
      {ihiMethod === 'manual' && (
        <IHIFill {...props} isDisabled={isDisabled} setIhiMethod={setIhiMethod} />
      )}
    </>
  )
}

type IHIGetterProps = {
  originalIhiRecord?: Omit<IHIRecord, 'created_at'>
  effectivePrescriber: LimitedPrescriberProfile | Prescriber | null
} & IHIBuilderProps

const MedicareCard = ({
  originalIhiRecord,
  defaultPatient,
  effectivePrescriber,
  isDisabled = false,
}: IHIGetterProps) => {
  const { currentClinic } = useAppSelector((state) => state.clinic)
  const [warning, setWarning] = useState<string | React.ReactNode>('')
  const { medicareValidationResult } = useAppSelector((state) => state.patientForm)
  const medicareCardRef = useRef<string | null>(null)
  const medicareIrnRef = useRef<string | null>(null)
  const validateTimer = useRef(0)
  const isRetry = useRef(false)
  const {
    trigger,
    register,
    formState: { errors },
    getValues,
    clearErrors,
  } = useFormContext()

  const [medicare_no, medicare_irn, given_names, family_name, date_of_birth, gender] = useWatch({
    name: ['medicare_no', 'medicare_irn', 'given_names', 'family_name', 'date_of_birth', 'gender'],
  })

  const dispatch = useAppDispatch()

  const { trigger: searchIhi, loading } = useApi(PatientClient.checkIhi, {
    onSuccess: async (response) => {
      clearErrors('ihi_number')
      const newRecord = formatIhiResponse(response, originalIhiRecord)
      if (newRecord.service_message?.codeField === '01611') {
        validateTimer.current++
        if (validateTimer.current < 3) {
          validateMedicareNumber()
          return
        }
      }
      validateTimer.current = 0
      trackIhiLookup('medicare', newRecord)
      dispatch(
        setMedicareValidationResult({
          ihi_status: newRecord.ihi_status,
          record_status: newRecord.record_status,
          ihi_number: newRecord.ihi_number,
          service_message: newRecord.service_message,
        })
      )
      setWarning(getWarning(newRecord, originalIhiRecord))
      medicareCardRef.current = medicare_no
      medicareIrnRef.current = medicare_irn
    },
    onFailure: () => {
      trackIhiLookup('medicare')
      setWarning(
        <Text>
          Sorry, validation failed. Please click{' '}
          <Link onClick={validateMedicareNumber} color="blue.500">
            here
          </Link>{' '}
          to try again.
        </Text>
      )
    },
  })

  const resetIhiStatus = () => {
    setWarning('')
    dispatch(resetMedicareValidationResult())
  }

  useIHIRevalidateFieldsChange({
    searchIhi,
    effectivePrescriber,
    medicareSearchParams: {
      MedicareIRN: medicare_irn,
      MedicareCardNumber: medicare_no,
    },
    resetValidation: resetIhiStatus,
  })

  useEffect(() => {
    medicareCardRef.current = defaultPatient?.medicare_no || null
    medicareIrnRef.current = defaultPatient?.medicare_irn || null
  }, [defaultPatient?.medicare_no, defaultPatient?.medicare_irn])

  const validateMedicareNumber = async () => {
    const isValid = await trigger([
      'given_names',
      'family_name',
      'gender',
      'date_of_birth',
      'medicare_irn',
      'medicare_no',
    ])

    if (
      !isValid ||
      !medicare_no ||
      !medicare_irn ||
      (medicare_no === medicareCardRef.current &&
        medicare_irn === medicareIrnRef.current &&
        !isRetry.current)
    ) {
      return
    }
    isRetry.current = false
    await searchIhi({
      GivenName: given_names,
      FamilyName: family_name,
      DOB: date_of_birth,
      Gender: gender as IhiGender,
      HpiiNumber: effectivePrescriber?.hpii ?? '',
      ClinicId: currentClinic?.id,
      MedicareIRN: medicare_irn || '',
      MedicareCardNumber: medicare_no || '',
    })
  }

  const { color, message, icon, messageColor } = formatIHIValidate({
    ...medicareValidationResult,
    isLoading: loading,
    warning,
    callback: () => {
      isRetry.current = true
      validateMedicareNumber()
    },
  })

  return (
    <Card
      display="grid"
      gridTemplateColumns="1fr 1fr"
      gap={2}
      p={4}
      onBlur={validateMedicareNumber}
      borderColor={color}
      borderWidth={1}
    >
      <InputField label="Medicare Card Number" field="medicare_no" errors={errors}>
        <Input
          {...register('medicare_no', {
            setValueAs: trimValue,
            validate: (medicareNo?: string) => {
              if (medicareNo && medicareNo.trim() === '') {
                return 'Medicare number cannot be empty if provided'
              }
              return medicareNumberValidator(
                medicareNo,
                getValues().dva_file_no,
                getValues().ihi_number
              )
            },
            deps: ['dva_file_no'],
            onChange: resetIhiStatus,
          })}
          disabled={isDisabled}
        />
      </InputField>
      <InputField label="Medicare IRN" field="medicare_irn" errors={errors}>
        <Flex alignItems="center" gap={4}>
          <Input
            {...register('medicare_irn', {
              setValueAs: trimValue,
              minLength: {
                value: 1,
                message: 'Please enter a valid IRN',
              },
              maxLength: {
                value: 1,
                message: 'Please enter a valid IRN',
              },
              validate: (irnNo: undefined | string) => {
                return medicareIrnValidator(irnNo, getValues().dva_file_no, getValues().ihi_number)
              },
              onChange: resetIhiStatus,
              deps: ['dva_file_no'],
            })}
            disabled={isDisabled}
          />
        </Flex>
      </InputField>
      {loading && (
        <Flex alignItems="center" gap={4} color="gray.500">
          Validating Medicare... <Spinner size="sm" />
        </Flex>
      )}
      <FormControl isInvalid={!!icon} gridColumn="1 / span 2">
        <FormErrorMessage display="flex" alignItems="center" gap={2} color={messageColor}>
          {icon} {message}
        </FormErrorMessage>
      </FormControl>
    </Card>
  )
}

const DvaCard = ({
  originalIhiRecord,
  defaultPatient,
  effectivePrescriber,
  isDisabled = false,
}: IHIGetterProps) => {
  const { dvaValidationResult } = useAppSelector((state) => state.patientForm)
  const {
    register,
    formState: { errors },
    getValues,
    trigger,
    clearErrors,
  } = useFormContext()
  const dispatch = useAppDispatch()
  const [warning, setWarning] = useState<string | React.ReactNode>('')
  const [given_names, family_name, date_of_birth, gender, dva_file_no] = useWatch({
    name: ['given_names', 'family_name', 'date_of_birth', 'gender', 'dva_file_no'],
  })
  const dvaFileNoRef = useRef<string | null>(null)
  const { currentClinic } = useAppSelector((state) => state.clinic)
  const validateTimer = useRef(0)
  const isRetry = useRef(false)

  const { trigger: searchIhi, loading } = useApi(PatientClient.checkIhi, {
    onSuccess: async (response) => {
      clearErrors('ihi_number')

      const newRecord = formatIhiResponse(response, originalIhiRecord)
      if (newRecord.service_message?.codeField === '01611') {
        validateTimer.current++
        if (validateTimer.current < 3) {
          validateDvaNumber()
          return
        }
      }
      validateTimer.current = 0
      trackIhiLookup('dva', newRecord)
      dispatch(
        setDvaValidationResult({
          ihi_status: newRecord.ihi_status,
          record_status: newRecord.record_status,
          ihi_number: newRecord.ihi_number,
          service_message: newRecord.service_message,
        })
      )
      setWarning(getWarning(newRecord, originalIhiRecord))
      dvaFileNoRef.current = dva_file_no
    },
    onFailure: () => {
      trackIhiLookup('dva')
      setWarning(
        <Text>
          Sorry, validation failed. Please click{' '}
          <Link onClick={validateDvaNumber} color="blue.500">
            here
          </Link>{' '}
          to try again.
        </Text>
      )
    },
  })

  const resetIhiStatus = () => {
    dispatch(resetDvaValidationResult())
    setWarning('')
  }

  useIHIRevalidateFieldsChange({
    searchIhi,
    effectivePrescriber,
    dvaSearchParams: {
      DvaFileNumber: dva_file_no,
    },
    resetValidation: resetIhiStatus,
  })

  useEffect(() => {
    dvaFileNoRef.current = defaultPatient?.dva_file_no || null
  }, [defaultPatient?.dva_file_no])

  const validateDvaNumber = async () => {
    const isValid = await trigger([
      'given_names',
      'family_name',
      'date_of_birth',
      'gender',
      'dva_file_no',
    ])
    if (!isValid || !dva_file_no || (dva_file_no === dvaFileNoRef.current && !isRetry.current)) {
      return
    }
    isRetry.current = false
    await searchIhi({
      GivenName: given_names,
      FamilyName: family_name,
      DOB: date_of_birth,
      Gender: gender as IhiGender,
      HpiiNumber: effectivePrescriber?.hpii ?? '',
      ClinicId: currentClinic?.id,
      DvaFileNumber: dva_file_no || '',
    })
  }

  const { color, message, icon, messageColor } = formatIHIValidate({
    ...dvaValidationResult,
    isLoading: loading,
    warning,
    callback: () => {
      isRetry.current = true
      validateDvaNumber()
    },
  })

  return (
    <Card
      display="grid"
      gridTemplateColumns="1fr 1fr"
      gap={2}
      p={4}
      borderColor={color}
      borderWidth={1}
    >
      <InputField label="DVA Card Number" field="dva_file_no" errors={errors}>
        <Input
          {...register('dva_file_no', {
            setValueAs: trimValue,
            validate: (dvaFileNo: undefined | string) => {
              return dvaFileNumberValidator(
                dvaFileNo,
                getValues().medicare_no,
                getValues().medicare_irn,
                getValues().ihi_number
              )
            },
            onChange: resetIhiStatus,
            onBlur: validateDvaNumber,
            deps: ['medicare_no', 'medicare_irn'],
          })}
          disabled={isDisabled}
        />
      </InputField>
      <InputField label="DVA Card Colour" field="dva_card_colour" errors={errors}>
        <Select
          {...register('dva_card_colour', {
            setValueAs: trimValue,
            validate: (dvaCardColour: undefined | string) => {
              const dvaCardNumber = getValues().dva_file_no
              if (dvaCardNumber) {
                return (
                  isValidDvaCardColour(String(dvaCardColour)) ||
                  'Please select a valid Dva Card Colour'
                )
              }
              return true
            },
            deps: ['dva_file_no'],
          })}
          disabled={isDisabled}
        >
          <option value=""></option>
          <option value="G">{DVA_CARD_COLOUR.G}</option>
          <option value="W">{DVA_CARD_COLOUR.W}</option>
          <option value="O">{DVA_CARD_COLOUR.O}</option>
        </Select>
      </InputField>
      {loading && (
        <Flex alignItems="center" gap={4} color="gray.500">
          Validating Dva... <Spinner size="sm" />
        </Flex>
      )}

      <FormControl isInvalid={!!icon} gridColumn="1 / span 2">
        <FormErrorMessage display="flex" alignItems="center" gap={2} color={messageColor}>
          {icon} {message}
        </FormErrorMessage>
      </FormControl>
    </Card>
  )
}

const IHIFill = ({
  setIhiMethod,
  originalIhiRecord,
  effectivePrescriber,
  isDisabled = false,
}: IHIGetterProps & {
  setIhiMethod: (method: 'generate' | 'manual') => void
}) => {
  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    getValues,
    watch,
  } = useFormContext()
  const { ihiValidationResult, medicareValidationResult, dvaValidationResult } = useAppSelector(
    (state) => state.patientForm
  )
  const [warning, setWarning] = useState<string | React.ReactNode>('')
  const dispatch = useAppDispatch()
  const [given_names, family_name, date_of_birth, gender, ihi_number] = useWatch({
    name: ['given_names', 'family_name', 'date_of_birth', 'gender', 'ihi_number'],
  })
  const { currentClinic } = useAppSelector((state) => state.clinic)
  const isMedicareAcceptable = formatIHIValidate(medicareValidationResult).isAcceptable
  const isDvaAcceptable = formatIHIValidate(dvaValidationResult).isAcceptable
  const validateTimer = useRef(0)

  const resetIhiStatus = () => {
    setWarning('')
    dispatch(resetIhiValidationResult())
  }

  useEffect(() => {
    if (isMedicareAcceptable) {
      setValue('ihi_number', medicareValidationResult.ihi_number || '')
    } else if (isDvaAcceptable) {
      setValue('ihi_number', dvaValidationResult.ihi_number || '')
    }
  }, [
    isMedicareAcceptable,
    isDvaAcceptable,
    setValue,
    medicareValidationResult,
    dvaValidationResult,
  ])

  useEffect(() => {
    const subscription = watch(async (values, { name }) => {
      const isTargetField = ['given_names', 'family_name', 'date_of_birth', 'gender'].includes(
        name as string
      )
      const isTargetFieldValid = await trigger([
        'given_names',
        'family_name',
        // 'date_of_birth', // date of birth validation has something wrong because of the register way, so just comment it out for now
        'gender',
      ])

      if (isTargetField && isTargetFieldValid && values.date_of_birth) {
        resetIhiStatus()
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, trigger])

  const { trigger: searchIhi, loading } = useApi(PatientClient.checkIhi, {
    onSuccess: async (response) => {
      const newRecord = formatIhiResponse(response, originalIhiRecord)
      if (newRecord.service_message?.codeField === '01611') {
        validateTimer.current++
        if (validateTimer.current < 3) {
          validateIhi()
          return
        }
      }
      validateTimer.current = 0
      trackIhiLookup('ihi', newRecord)
      dispatch(
        setIhiValidationResult({
          ihi_status: newRecord.ihi_status,
          record_status: newRecord.record_status,
          ihi_number: newRecord.ihi_number,
          service_message: newRecord.service_message,
        })
      )
      setWarning(getWarning(newRecord, originalIhiRecord))
    },
    onFailure: () => {
      trackIhiLookup('ihi')
      setWarning('Sorry, validation failed. Please try again.')
    },
  })

  const validateIhi = async () => {
    const isValid = await trigger(['given_names', 'family_name', 'date_of_birth', 'ihi_number'])
    if (!isValid || !ihi_number) {
      return
    }
    resetIhiStatus()
    await searchIhi({
      GivenName: given_names,
      FamilyName: family_name,
      DOB: date_of_birth,
      Gender: gender as IhiGender,
      IhiNumber: ihi_number,
      HpiiNumber: effectivePrescriber?.hpii ?? '',
      ClinicId: currentClinic?.id,
    })
  }

  const { message, icon, messageColor } = formatIHIValidate({
    ...ihiValidationResult,
    isLoading: loading,
    warning,
    customError: 'Sorry, validation timed out. Please try again.',
  })

  return (
    <VStack flex="1" alignItems="flex-start">
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Heading as="h4" size="md">
          Individual Healthcare Identifier (IHI)
        </Heading>
        <Button
          variant="link"
          color="blue.500"
          size="sm"
          onClick={() => {
            setIhiMethod('generate')
          }}
          {...analyticsAction('ihi_back_to_medicare_dva')}
        >
          Back to Medicare/DVA Information
        </Button>
      </Flex>
      <Text color="gray.500">You must validate your patient's IHI</Text>
      <SimpleGrid
        spacingX={8}
        alignItems={errors.ihi_number ? 'center' : 'flex-end'}
        templateColumns="2fr 1fr"
        width="100%"
      >
        <InputField
          label="IHI Number"
          field="ihi_number"
          errors={errors}
          _placeholder={{ color: 'gray.500' }}
          isRequired
        >
          <InputGroup>
            <Input
              placeholder="Enter 16-digit IHI number"
              {...register('ihi_number', {
                onChange: resetIhiStatus,
                setValueAs: trimValue,
                validate: (ihi: number | string) => {
                  ihi = String(ihi || '')
                  if (ihi && ihi.trim() === '') {
                    return 'IHI number cannot be empty if provided'
                  }
                  return ihiNumberValidator({
                    ihi_number: ihi,
                    medicare_no: getValues().medicare_no,
                    medicare_irn: getValues().medicare_irn,
                    dva_file_no: getValues().dva_file_no,
                  })
                },
              })}
              disabled={isDisabled || isMedicareAcceptable || isDvaAcceptable}
            />
            {loading && (
              <InputRightElement>
                <Spinner size="sm" />
              </InputRightElement>
            )}
            {icon && <InputRightElement>{icon}</InputRightElement>}
          </InputGroup>
        </InputField>
        {!(isMedicareAcceptable || isDvaAcceptable) && (
          <Button
            onClick={validateIhi}
            disabled={loading || !ihi_number || isDisabled}
            marginEnd={8}
            colorScheme="blue"
            {...analyticsAction('ihi_validate')}
          >
            Validate IHI
          </Button>
        )}
        <FormControl isInvalid={!!icon && !errors.ihi_number}>
          <FormErrorMessage color={messageColor}>{message}</FormErrorMessage>
        </FormControl>
      </SimpleGrid>
    </VStack>
  )
}

export default IHIBuilder
